// Mock data for portfolio
export const personalInfo = {
  name: "Nurettin Fatih AKKIN",
  title: "Software Engineer",
  subtitle: "ERP & Production Systems | Backend Architecture",
  description: "I specialize in designing backend and ERP systems that address real operational challenges, building clean, scalable, and production-ready architectures.",
  location: "Turkey",
  email: "nfatihakkin@gmail.com",
  linkedin: "https://www.linkedin.com/in/nfatihakkin",
  github: "https://github.com/nfatihakkin",
  portfolio: "https://nfatihakkin.com"
};

export const experiences = [
  {
    id: 1,
    company: "Kobi Muhendislik Danismanlik Ticaret A.S.",
    position: "Mid-Level Software Engineer",
    duration: "2025 - Present",
    location: "Gebze, Kocaeli",
    description: "Led backend development of a real-time MES (Manufacturing Execution System) operating across 100+ machines, processing second-by-second production data to enhance manufacturing efficiency and deliver actionable insights for process optimization. Focused on integrating live factory data with ERP systems and driving measurable productivity improvements through intelligent automation and analytics.",
    technologies: ["C# / .NET 8", "Angular", "DevExpress", "SignalR", "Docker", "Hangfire", "SQL Server", "PostgreSQL", "Clean Architecture"],
    achievements: [
      "Developed a real-time messaging architecture using SignalR, enabling operator dashboards to instantly react to live data from over 100 machines, fully synchronized with ERP.",
      "Containerized and optimized background processes with Hangfire and Docker, improving observability, scalability, and reliability of asynchronous workloads.",
      "Implemented optimization algorithms to streamline software workflows and reduce computational overhead across production planning modules.",
      "Implemented CI/CD pipelines for automated publishing, monitoring, and versioned deployments of .NET applications.",
      "Built a cross-platform operator control system enabling real-time production data visualization and process tracking.",
      "Developed a financial reconciliation platform using Angular and .NET Web API, automating matching logic and enhancing reporting accuracy across enterprise accounts."
    ]
  },
  {
    id: 2,
    company: "Kobi Muhendislik Danismanlik Ticaret A.S.",
    position: "Software Engineer",
    duration: "2022 - 2025",
    location: "Gebze, Kocaeli",
    description: "Contributed to the development and deployment of large-scale ERP modules and MES-integrated production systems. Actively involved in the full DevOps lifecycle, from backend API development to CI/CD publishing pipelines and mobile application integration. Built real-time operator control software and implemented asynchronous communication through RabbitMQ to achieve stable, event-driven workflows across multiple factory units.",
    technologies: ["C# / .NET 6+", "MSSQL", "RabbitMQ", "Docker", "DevExpress", "DevOps", "API Design", "Mobile Integration"],
    achievements: [
      "Developed and deployed ERP modules supporting production, planning, and material management, fully integrated with MES.",
      "Integrated RabbitMQ for reliable inter-service communication and event-based production notifications.",
      "Contributed to mobile app and API design, improving field data entry and operator accessibility."
    ]
  },
  {
    id: 3,
    company: "DATASURGERY Yazilim ve Danismanlik Ltd. Sti.",
    position: "Software Engineer Intern",
    duration: "2022/01 - 2022/05",
    location: "Istanbul",
    description: "Worked in a cross-functional Agile/Scrum team, contributing to full-stack development processes and modern engineering practices. Gained hands-on experience in Git-based version control, branch strategies, and collaborative workflows. Focused on API design following best practices, MVC architecture, and clean code principles while actively participating in sprint reviews, retrospectives, and code reviews.",
    technologies: ["C#", ".NET Core", "MVC", "Git", "Agile/Scrum"],
    achievements: [
      "Collaborated within an Agile/Scrum environment, taking part in sprint planning, daily stand-ups, and retrospective meetings.",
      "Designed RESTful APIs using industry best practices, ensuring scalability and maintainability.",
      "Implemented Git branching strategies and peer code review workflows for structured collaboration.",
      "Developed MVC-based components and contributed to backend modules using C#/.NET Core."
    ]
  }
];

// Fallback only — shown if the live GitHub fetch fails or is rate-limited.
// No fabricated stars/forks or placeholder demo links: what visitors see here
// should never overstate what the repos actually are.
export const projects = [
  {
    id: 1,
    name: "advanced-production-scheduler",
    description: "Advanced Production Scheduling (APS) module: algorithmic planning for manufacturing throughput, built on .NET.",
    language: "C#",
    stars: 0,
    forks: 0,
    topics: ["production-scheduling", "manufacturing", "dotnet"],
    homepage: "",
    lastUpdated: "2025-01-15"
  },
  {
    id: 2,
    name: "modular-erp-core",
    description: "Modular ERP core for industrial workflows — clean architecture, maintainable boundaries, .NET.",
    language: "C#",
    stars: 0,
    forks: 0,
    topics: ["erp", "clean-architecture", "dotnet"],
    homepage: "",
    lastUpdated: "2025-01-18"
  },
  {
    id: 3,
    name: "mes-realtime-gateway",
    description: "Real-time gateway streaming machine telemetry to operator dashboards over SignalR.",
    language: "C#",
    stars: 0,
    forks: 0,
    topics: ["mes", "signalr", "real-time"],
    homepage: "",
    lastUpdated: "2025-01-12"
  }
];

// Hand-picked, shipped projects shown in the Featured section.
export const featuredProjects = [
  {
    id: "shortly",
    name: "Shortly",
    kind: "SaaS · Link management",
    description:
      "A focused link-management platform for SaaS teams: branded short links, real-time click analytics, role-based workspaces, and Stripe billing with per-plan limits.",
    tech: ["React", "TypeScript", "Stripe", "Analytics", "TR / EN"],
    live: "https://shortly.nfatihakkin.com",
    image: "/projects/shortly.png"
  },
  {
    id: "bastion",
    name: "Bastion",
    kind: "Product · Habit tracking",
    description:
      "A calm productivity app built on one idea — stability over motivation. No gamification, offline-first, bilingual. Shown here through its editorial landing site.",
    tech: ["React", "PWA", "Offline-first", "TR / EN"],
    live: "https://tracker.nfatihakkin.com",
    image: "/projects/bastion.png"
  },
  {
    id: "lab",
    name: "NFA Lab",
    kind: "Embedded · Learning log",
    description:
      "A build-log for learning embedded systems from scratch on the TM4C123 — each project a clear goal, wiring diagram, code, and a working output. Open source, bilingual.",
    tech: ["Next.js", "TM4C123", "Embedded C", "TR / EN"],
    live: "https://lab.nfatihakkin.com",
    image: "/projects/lab.png"
  },
  {
    id: "games",
    name: "NFA Games",
    kind: "Portal · Browser games",
    description:
      "A home for original browser games — no downloads, no waiting. Play instantly on desktop or mobile: an underwater arena, an evolution eat-'em-up, and a word puzzle.",
    tech: ["Web games", "Canvas", "Multiplayer", "Mobile"],
    live: "https://game.nfatihakkin.com",
    image: "/projects/games.png"
  }
];

export const skills = [
  {
    category: "Languages",
    items: ["C#", ".NET Core", "Python", "TypeScript", "SQL", "JavaScript"]
  },
  {
    category: "Frameworks & Libraries",
    items: [".NET Framework", "ASP.NET Core", "Entity Framework", "FastAPI", "React", "Angular"]
  },
  {
    category: "Cloud & Infrastructure",
    items: ["Azure", "AWS", "Docker", "Kubernetes", "CI/CD", "DevOps"]
  },
  {
    category: "Databases",
    items: ["SQL Server", "PostgreSQL", "MongoDB", "Redis", "Oracle"]
  },
  {
    category: "Specializations",
    items: ["ERP Systems", "Production Planning", "AI Scheduling", "Backend Architecture", "System Integration"]
  }
];

export const achievements = [
  "Specialized in ERP and Production Systems architecture",
  "Developing AI-assisted Advanced Production Scheduling (APS) module",
  "Expert in .NET and C# backend architecture",
  "Building modular ERP components for industrial use cases",
  "Optimizing manufacturing processes through algorithmic planning",
  "Creating sustainable, clean and maintainable systems",
  "Focused on tangible business outcomes over just writing code",
  "Detail-oriented professional committed to industry best practices"
];
